<template>
  <div class="container-fluid admin-page">
    <div class="title-area">
      <h2>付箋種別管理</h2>
      <div class="create-btn">
        <button class="btn btn-info" @click="create">
          <i class="fas fa-plus"></i><span class="ml-1">新規作成</span>
        </button>
      </div>
    </div>
    <AdminListTable
      :display-data="state.commentTypes"
      :fields="fields"
      @start-edit="edit"
      @start-destroy="tryDestroy"
    />
    <Modal
      v-if="state.showEditModal"
      :title="editModalTitle"
      bottom-close-button-label="保存"
      :isCloseButtonDisabled="!canSave"
      @close="save"
      @dismiss="state.showEditModal = false"
    >
      <template v-slot:body>
        <div>
          <div class="form-group">
            <label class="form-control-label">
              種別名
            </label>
            <input
              type="text"
              class="form-control"
              v-model="state.editting.commentType"
              :readonly="state.editMode === 'edit'"
            />
          </div>
          <div class="form-group">
            <label class="form-control-label">
              表示名
            </label>
            <input type="text" class="form-control" v-model="state.editting.commentTypeDisp" />
          </div>
          <div class="form-group">
            <label class="form-control-label">
              アイコン
            </label>
            <div class="icon-options mt4">
              <div
                v-for="e in state.iconPaths"
                :key="e"
                class="icon-box"
                :class="{ 'selected': e === state.editting.iconPath }"
              >
                <img class="resource-icon" :src="e" @click="selectIcon(e)"/>
              </div>
            </div>
          </div>
          <div class="form-group">
            <label class="form-control-label">
              表示順
            </label>
            <input type="text" class="form-control" v-model="state.editting.dispOrder" />
          </div>
          <div class="form-group errors">
            <div class="error" v-for="(e, i) in state.httpErrorMsgs" :key="i">
              {{e}}
            </div>
          </div>
        </div>
      </template>
    </Modal>
    <Modal
      v-if="state.showDestroyModal"
      :title="destroyModalTitle"
      :show-bottom-dismiss-button="true"
      @close="doDestroy"
      @dismiss="state.showDestroyModal = false"
    >
      <template v-slot:body>
        <div>
          <p class="p1">付箋種別<span class="colon"></span>{{state.editting.commentTypeDisp}}</p>
          <p class="p2 font-mtx-desc" v-html="destroyModalMsg"></p>
        </div>
      </template>
    </Modal>
    <Modal
      v-if="state.showErrorModal"
      :title="errorModalTitle"
      :show-bottom-dismiss-button="false"
      @close="state.showErrorModal = false"
    >
      <template v-slot:body>
        <div>
          <p class="p2 font-mtx-desc" v-html="state.errorModalMsg"></p>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import type { AxiosError } from 'axios';
import commentTypeApi from 'src/apis/commentType';
import {
  computed,
  onMounted,
  reactive,
} from 'vue';
import type { CommentType } from 'src/models/master';
import { getErrorMessages } from 'src/lib/errMsgHelper';
import { useReactiveMasters } from 'src/composables/useMasters';

interface AdminCommentTypeEdit {
  id: number;
  group1Id?: number;
  commentType: string;
  commentTypeDisp: string;
  dispOrder: number;
  iconPath: string;
}

interface AdminCommentTypeState {
  showEditModal: boolean;
  editMode: 'edit' | 'create';
  commentTypes: CommentType[];
  editting: AdminCommentTypeEdit;
  httpErrorMsgs: string[];
  iconPaths: string[];
  showDestroyModal: boolean;
  showErrorModal: boolean;
  errorModalMsg: string;
}

const initEdittingState = (): AdminCommentTypeEdit => ({
  id: 0,
  commentType: '',
  commentTypeDisp: '',
  dispOrder: 0,
  iconPath: '',
});
const state = reactive<AdminCommentTypeState>({
  showEditModal: false,
  editMode: 'edit',
  commentTypes: [],
  editting: initEdittingState(),
  httpErrorMsgs: [],
  iconPaths: [],
  showDestroyModal: false,
  showErrorModal: false,
  errorModalMsg: '不明なエラーです。ネットワークの状態をご確認いただき再度お試し下さい。',
});

const canSave = computed<boolean>(() => {
  const hasCommentType = !!state.editting.commentType;
  const hasCommentTypeDisp = !!state.editting.commentTypeDisp;
  const hasIconPath = !!state.editting.iconPath;
  const hasNumericDispOrder = !!state.editting.dispOrder &&
    /^\d+$/.test(state.editting.dispOrder.toString());
  return hasCommentType && hasCommentTypeDisp && hasIconPath && hasNumericDispOrder;
});
const editModalTitle = computed<string>(() => {
  return state.editMode === 'create' ? '新規作成' : '編集';
});

const { masters, resolveMastersPromise, refreshCommentType } = useReactiveMasters();

onMounted(async() => {
  await resolveMastersPromise();
  state.iconPaths = masters.comment.commentTypeIconPaths;
  doSearch();
});

const doSearch = async() => {
  const commentTypes = await commentTypeApi.adminIndex();
  state.commentTypes = commentTypes;
};
const selectIcon = (path: string) => {
  state.editting.iconPath = path;
};
const edit = (obj: CommentType) => {
  state.editting = { ...obj };
  state.httpErrorMsgs = [];
  state.editMode = 'edit';
  state.showEditModal = true;
};
const create = () => {
  const unusedIconPaths = state.commentTypes.reduce((iconPaths: string[], commentType) => {
    return iconPaths.filter(iconPath => iconPath !== commentType.iconPath);
  }, state.iconPaths.slice());
  const maxDispOrder = state.commentTypes.reduce((max, curr) => {
    return curr.dispOrder > max ? curr.dispOrder : max;
  }, 0);
  state.editting = {
    id: 0,
    commentType: '',
    commentTypeDisp: '',
    iconPath: unusedIconPaths.length > 0 ? unusedIconPaths[0] : state.iconPaths[0],
    dispOrder: maxDispOrder + 1,
  };
  state.editMode = 'create';
  state.showEditModal = true;
};
const save = async() => {
  try {
    if (state.editMode === 'edit') {
      await commentTypeApi.adminUpdate(state.editting.id, {
        comment_type_disp: state.editting.commentTypeDisp,
        icon_path: state.editting.iconPath,
        disp_order: state.editting.dispOrder,
      });
    } else {
      await commentTypeApi.adminCreate({
        comment_type: state.editting.commentType,
        comment_type_disp: state.editting.commentTypeDisp,
        icon_path: state.editting.iconPath,
        disp_order: state.editting.dispOrder,
      });
    }
    state.showEditModal = false;
    searchAndRefreshCommentTypeMaster();
  } catch (err) {
    state.httpErrorMsgs = getErrorMessages(err as AxiosError);
  }
};
const tryDestroy = (obj: CommentType) => {
  state.editting = obj;
  state.showDestroyModal = true;
};
const doDestroy = async() => {
  try {
    await commentTypeApi.adminDestroy(state.editting.id);
    state.showDestroyModal = false;
    searchAndRefreshCommentTypeMaster();
  } catch (err) {
    const errMsgs = getErrorMessages(err as AxiosError);
    if (errMsgs.includes('has comments')) {
      state.errorModalMsg = '1件以上の付箋が登録されているので削除できません。';
    } else if (errMsgs.includes('last comment_type')) {
      state.errorModalMsg = '最後の付箋種別は削除できません。';
    } else {
      state.errorModalMsg = '不明なエラーです。ネットワークの状態をご確認いただき再度お試し下さい。';
    }
    state.showDestroyModal = false;
    state.showErrorModal = true;
  }
};
const searchAndRefreshCommentTypeMaster = async() => {
  await doSearch();
  refreshCommentType(state.commentTypes);
};

const fields = [
  { name: 'commentType', label: '種別名' },
  { name: 'commentTypeDisp', label: '表示名' },
  { name: 'iconPath', label: 'アイコン', type: 'icon' },
  { name: 'dispOrder', label: '表示順' },
];
const destroyModalTitle = '削除';
const destroyModalMsg = 'を削除してもよろしいですか？';
const errorModalTitle = 'エラー';

</script>

<style lang="scss"  scoped>
.resource-icon {
  width: 48px;
}
.icon-options {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  .icon-box {
    cursor: pointer;
    position: relative;
    border: 1px solid #dddddd;
    border-radius: 4px;
    margin: 0 8px 8px 0;
    opacity: 0.5;
    background-color: #dddddd;
    &.selected {
      outline: 4px solid #f00;
      border-radius: 2px;
      opacity: 1.0;
      background-color: transparent;
    }
  }
}
</style>
